import React, { useEffect, useState } from "react";
import { Box, TextField, Button, Stack, Snackbar, Alert, CircularProgress } from "@mui/material";
import axios from '../../Configs/axiosInstance';
import { MAIN_URL } from "../../Configs/Urls";
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import { Section, FieldRow, ChoiceField } from "../PatientsPage/Components/PatientFormPieces";

// Utility function to format a date as yyyy-mm-dd (what <input type="date">
// expects), defaulting to today when no date is given.
const getFormattedDate = (date) => {
  const d = date ? new Date(date) : new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export default function PaymentFormPage({ name, patientId, paymentId, onCloseForm }) {

  const [PaymentData, setPaymentData] = useState({
    PatientId: patientId,
    amount: '',
    paymentDate: getFormattedDate(null),
    paymentType: "",
    purpose: ""
  });

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (paymentId) {
      axios
        .get(`${MAIN_URL}payments/${paymentId}`)
        .then((response) => {
          setPaymentData({
            ...response.data,
            paymentDate: getFormattedDate(response.data.paymentDate),
          });
        })
        .catch(() => {
          setSnackbarMessage("Error fetching payment data.");
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
        });
    }
  }, [paymentId]);

  const handleInputChange = (event) => {
    setErrors({});
    const { name, value } = event.target;
    setPaymentData((prevData) => ({ ...prevData, [name]: value }));
  };

  const setPaymentType = (value) => {
    setErrors({});
    setPaymentData((prevData) => ({ ...prevData, paymentType: value }));
  };

  const validate = () => {
    const newErrors = {};

    if (!PaymentData.amount || PaymentData.amount < 1)
      newErrors.amount = "Amount must be a positive number.";
    if (!PaymentData.paymentType)
      newErrors.paymentType = "Payment type is required.";
    if (!PaymentData.purpose)
      newErrors.purpose = "Purpose is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validate()) return;

    const payload = { ...PaymentData, paymentDate: new Date(PaymentData.paymentDate) };
    const method = paymentId ? "put" : "post";

    if (method === "post") {
      payload.payerName = name;
    }

    const url = paymentId
      ? `${MAIN_URL}payments/${paymentId}`
      : `${MAIN_URL}payments`;

    setLoading(true);

    axios[method](url, payload)
      .then(() => {
        setSnackbarMessage(paymentId ? "Payment updated successfully!" : "Payment added successfully!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);

        setTimeout(() => {
          onCloseForm();
        }, 1200);
        setLoading(false);
      })
      .catch((error) => {
        setSnackbarMessage(error?.response?.data?.message ? error?.response.data.message : "Error submitting form. Please try again.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        console.error("Error submitting form:", error);
        setLoading(false);
      });
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  return (
    <Box sx={{ width: "100%" }}>
      <form onSubmit={handleSubmit}>
        <Section
          icon={<PaidRoundedIcon />}
          title={paymentId ? "Edit Payment" : "Add Payment"}
          subtitle={paymentId ? "Update this payment's details" : "Record a new payment for this patient"}
        >
          <FieldRow columns={2}>
            <TextField
              fullWidth
              name="amount"
              variant="outlined"
              label="Amount"
              type="number"
              value={PaymentData.amount}
              onChange={handleInputChange}
              error={!!errors.amount}
              helperText={errors.amount}
              required
            />
            <TextField
              fullWidth
              name="paymentDate"
              variant="outlined"
              label="Payment Date"
              type="date"
              value={PaymentData.paymentDate}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              required
            />
          </FieldRow>

          <ChoiceField
            label="Payment Type"
            error={errors.paymentType}
            options={["Cash", "Online", "Other"]}
            value={PaymentData.paymentType}
            onChange={setPaymentType}
          />

          <TextField
            fullWidth
            name="purpose"
            variant="outlined"
            label="Purpose"
            placeholder="Enter the purpose or any comments"
            multiline
            minRows={2}
            value={PaymentData.purpose}
            onChange={handleInputChange}
            error={!!errors.purpose}
            helperText={errors.purpose}
            required
          />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PaidRoundedIcon />}
              sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}
            >
              {paymentId
                ? (loading ? "Updating..." : "Update Payment")
                : (loading ? "Adding..." : "Add Payment")}
            </Button>
          </Stack>
        </Section>
      </form>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant="filled" sx={{ fontWeight: 600 }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
